import { container, singleton } from 'tsyringe';
import type { ActionCategory, ActionDefinition, ActionId } from './action.types';

/**
 * Central registry for frontend UI actions.
 *
 * Maps ActionIds to ActionDefinitions and provides execute/query API.
 * Inspired by VS Code's action registry pattern.
 *
 * The registry is structured so that an `executed$: Observable<{actionId: ActionId, success: boolean}>`
 * Subject can be added later without breaking changes.
 *
 * @singleton - One instance for the entire application lifetime.
 */
@singleton()
export class ActionRegistryImpl {
    private readonly _actions = new Map<ActionId, ActionDefinition<any>>();

    /**
     * Registers an action definition in the registry.
     *
     * @param definition - The action definition to register.
     * @throws Error if an action with the same id is already registered.
     */
    register<T>(definition: ActionDefinition<T>): void {
        if (this._actions.has(definition.id)) {
            throw new Error(`Action '${definition.id}' is already registered. Duplicate registration is not allowed.`);
        }
        this._actions.set(definition.id, definition);
    }

    /**
     * Executes a registered action by its ID.
     *
     * Checks canExecute() before calling execute(). Catches and logs any errors
     * thrown by canExecute() or execute() to prevent application crashes.
     *
     * @param actionId - The ID of the action to execute.
     * @param payload - Optional payload for parameterized actions.
     * @returns true if the action executed successfully, false otherwise.
     */
    async execute<T = void>(actionId: ActionId, payload?: T): Promise<boolean> {
        const action = this._actions.get(actionId);
        if (!action) {
            return false;
        }

        try {
            const enabled = this._checkCanExecute(action);
            if (!enabled) {
                return false;
            }
        } catch (error) {
            console.error(`ActionRegistry: canExecute() threw for action '${actionId}':`, error);
            return false;
        }

        try {
            await action.execute(payload as T);
            return true;
        } catch (error) {
            console.error(`ActionRegistry: execute() threw for action '${actionId}':`, error);
            return false;
        }
    }

    /**
     * Retrieves an action definition by its ID.
     *
     * @param actionId - The ID of the action to retrieve.
     * @returns The ActionDefinition, or undefined if not found.
     */
    get(actionId: ActionId): ActionDefinition | undefined {
        return this._actions.get(actionId);
    }

    /**
     * Returns whether an action exists and its canExecute() returns true.
     *
     * @param actionId - The ID of the action to check.
     * @returns true if the action exists and is enabled, false otherwise.
     */
    isEnabled(actionId: ActionId): boolean {
        const action = this._actions.get(actionId);
        if (!action) {
            return false;
        }

        try {
            return this._checkCanExecute(action);
        } catch (error) {
            console.error(`ActionRegistry: canExecute() threw for action '${actionId}':`, error);
            return false;
        }
    }

    /**
     * Returns all registered actions in a given category.
     *
     * @experimental May be restructured in future PRDs.
     * @param category - The category to filter by.
     * @returns Array of ActionDefinitions with the given category.
     */
    getByCategory(category: ActionCategory): ActionDefinition[] {
        return Array.from(this._actions.values()).filter(
            (action) => action.category === category
        );
    }

    /**
     * Checks whether an action's canExecute() returns true.
     * If canExecute is not defined, the action is considered always enabled.
     */
    private _checkCanExecute(action: ActionDefinition<any>): boolean {
        if (!action.canExecute) {
            return true;
        }
        return action.canExecute();
    }
}

/**
 * Module-level singleton instance of ActionRegistryImpl.
 *
 * Import this constant to access the action registry from toolbar items and other modules.
 * Actions are registered via registerActions() called from actions/index.ts (side-effect import in main.ts).
 */
export const actionRegistry = container.resolve(ActionRegistryImpl);
